/*
  tcl-bar-autohide — hide the sticky listing bar while the reader scrolls DOWN,
  bring it back after ~100px of scrolling UP.

  Applies to every `[data-tcl-autohide]` bar: the price list's own bar and the
  three that render `snippets/tcl-filter-bar.liquid` (/pages/treatments,
  /collections/*, the blog index).

  WHY ITS OWN FILE RATHER THAN A BLOCK IN tcl-filter-bar.js. The blog index
  carries the bar in SERVER mode — real links, a real GET form — and therefore
  deliberately never loads tcl-filter-bar.js. Putting this there would silently
  skip one of the four pages the behaviour was asked for. It is also not the
  price list's file, which is a section-level script for a different component.
  One behaviour, one file, four hosts.

  THE MODEL, and why each number is what it is:

    - DIRECTION IS ACCUMULATED, NOT SAMPLED. A trackpad emits a stream of
      small deltas that change sign; acting on one frame's delta makes the bar
      flicker. `down` and `up` accumulate travel in one direction and reset when
      the direction changes, so both thresholds are about a real gesture.
    - HIDE_AFTER is small (16px). Hiding is the cheap direction: the reader is
      moving away from the controls and can undo it by scrolling up.
    - SHOW_AFTER is 100px, as specified. Showing is the expensive direction — an
      unwanted reveal covers the rows being read — so it takes a deliberate
      gesture, not a stray upward nudge.
    - ABOVE THE BAR'S OWN PLACE IN THE PAGE THE BAR IS ALWAYS SHOWN. The
      transform moves it off-screen whether or not it is stuck, so a hidden class
      left on while the reader scrolls back to the top would strand the bar above
      a page that is no longer scrolled. The floor is the bar's own flow
      position, and it is CACHED FROM A MOMENT WHEN THE BAR WAS DEMONSTRABLY NOT
      STUCK — `rect.top + scrollY` while `rect.top` is below the sticky offset.

      NOT `offsetTop`. For a `position: sticky` element `offsetTop` reports the
      element's CURRENT position, stuck offset included, so a floor built from it
      rises with the scroll and `scrollY <= floor` is true forever: the bar never
      hides, on any page, while every other assertion still passes. Measured —
      this probe caught it.
    - FOCUS WINS. Tabbing into a hidden bar reveals it; `pointer-events: none`
      does not block keyboard focus, so without this a keyboard user would type
      into a search field they cannot see.
    - REDUCED MOTION DISABLES IT. A bar that teleports is a worse answer than a
      bar that stays.

  IT PUBLISHES `--tl-bar-visible-h` ON THE ROOT — the bar's height while shown,
  `0px` while hidden. The price list pins each table's `<thead>` under the bar
  (`top: calc(header + bar)`); without this the band the bar vacated would stay
  reserved and rows would scroll visibly through the gap between the header and
  the stuck heading. It is a SECOND variable rather than a redefinition of the
  price list's own `--tl-plist-bar`, which stays the measured height: the anchor
  offset for `#category` deep links must keep clearing a bar that is about to
  come back, so the two consumers genuinely want different numbers.

  It is never published as `0px` while the bar is shown. Both bars are
  `display: none` until their own script marks the root enhanced, so a height
  read before that is 0 and would pin the headings under the header for as long
  as it took the other script to run. A ResizeObserver re-publishes when the bar
  gains its height, changes rows at a breakpoint, or the Theme Editor reloads it.

  NO INTERSECTIONOBSERVER SENTINEL. The obvious implementation puts an empty
  `<div>` above the bar and watches it; this theme hides `div:empty` globally,
  which has already killed sentinels once.
*/
(function () {
  if (window.tclBarAutohide) return;
  window.tclBarAutohide = true;

  var SEL = '[data-tcl-autohide]';
  var HIDE_AFTER = 16;
  var SHOW_AFTER = 100;

  var reduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  var hidden = false;
  var lastY = window.pageYOffset || 0;
  var down = 0;
  var up = 0;
  var queued = false;

  function bars() {
    return Array.prototype.slice.call(document.querySelectorAll(SEL));
  }

  /* The bar's flow position, learned the first time it can be observed unstuck
     and then kept — it is a constant of the layout. `null` until then, which on
     a page opened at a deep link may be for the whole visit; hiding is allowed
     in that case, because a bar that is already stuck on arrival is by
     definition below its own flow position. */
  var flow = null;

  function learnFlow(bar, y) {
    var r = bar.getBoundingClientRect();
    if (r.height === 0) return;
    /* The RESOLVED sticky offset, not `--header-height`: the two agree today and
       a bar that ever parks somewhere else would still be measured correctly. */
    var stickyTop = parseFloat(getComputedStyle(bar).top) || 0;
    if (r.top > stickyTop + 1) flow = r.top + y;
  }

  function publish(list) {
    if (hidden) {
      document.documentElement.style.setProperty('--tl-bar-visible-h', '0px');
      return;
    }
    var h = 0;
    for (var i = 0; i < list.length; i++) {
      h = Math.max(h, list[i].getBoundingClientRect().height);
    }
    /* A shown bar with no height has not been enhanced yet — say nothing rather
       than publish a number that is about to be wrong. */
    if (h > 0) document.documentElement.style.setProperty('--tl-bar-visible-h', Math.round(h) + 'px');
  }

  function setHidden(list, next) {
    if (next !== hidden) {
      hidden = next;
      for (var i = 0; i < list.length; i++) list[i].classList.toggle('is-bar-hidden', hidden);
    }
    publish(list);
  }

  function measure() {
    queued = false;
    var list = bars();
    if (!list.length) return;

    if (reduce && reduce.matches) {
      down = 0;
      up = 0;
      setHidden(list, false);
      return;
    }

    var y = window.pageYOffset || 0;
    var d = y - lastY;
    lastY = y;

    if (!hidden) {
      for (var i = 0; i < list.length; i++) learnFlow(list[i], y);
    }
    if (flow !== null && y <= flow) {
      down = 0;
      up = 0;
      setHidden(list, false);
      return;
    }

    if (d > 0) {
      down += d;
      up = 0;
    } else if (d < 0) {
      up -= d;
      down = 0;
    }

    if (!hidden && down >= HIDE_AFTER) setHidden(list, true);
    else if (hidden && up >= SHOW_AFTER) setHidden(list, false);
    else publish(list);
  }

  function onScroll() {
    if (queued) return;
    queued = true;
    if (window.requestAnimationFrame) window.requestAnimationFrame(measure);
    else setTimeout(measure, 16);
  }

  function observe() {
    bars().forEach(function (bar) {
      if (bar.dataset.tclAhObserved || !window.ResizeObserver) return;
      bar.dataset.tclAhObserved = '1';
      new ResizeObserver(function () {
        publish(bars());
      }).observe(bar);
    });
    var list = bars();
    if (!hidden) {
      var y = window.pageYOffset || 0;
      for (var i = 0; i < list.length; i++) learnFlow(list[i], y);
    }
    publish(list);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  document.addEventListener('focusin', function (e) {
    if (e.target.closest && e.target.closest(SEL)) {
      down = 0;
      up = 0;
      setHidden(bars(), false);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe);
  } else {
    observe();
  }
  document.addEventListener('shopify:section:load', observe);
})();
